import { Injectable } from '@angular/core';
import type { Type } from '@angular/core';
import createDebug from 'debug';

const debug = createDebug('EventStaffing:ComponentService');

export interface ComponentMetadata {
  name: string;
  selector: string;
  displayName: string;
  description: string;
  inputs: readonly { name: string; type: string }[];
  outputs: readonly { name: string; type: string }[];
  isDialog: boolean;
  isCustomElement: boolean;
  allowChildren: boolean;
  metadata?: Record<string, unknown>;
}

@Injectable({ providedIn: 'root' })
export class ComponentService {
  readonly names = [
    'AddButton',
    'AddDialog',
    'AssignFunctionalArea',
    'Avatar',
    'Breadcrumb',
    'BreadcrumbLevel',
    'CandidateApplicationForm',
    'ClaimServiceTaskButton',
    'ClaimServiceTaskDialog',
    'ClaimUserTaskButton',
    'ClaimUserTaskDialog',
    'CompleteApplication',
    'CompleteServiceTaskButton',
    'CompleteServiceTaskDialog',
    'CompleteUserTaskButton',
    'CompleteUserTaskDialog',
    'EmailTester',
    'EvaluateInterview',
    'FireTimerButton',
    'FireTimerDialog',
    'HistoryDetailsDialog',
    'HistoryList',
    'LoadFromFilesButton',
    'LoadFromFilesDialog',
    'ManageRoleAssignments',
    'OrganizationDetailsDialog',
    'OrgStructureLevelDetailsDialog',
    'PerformInterview',
    'PositionDetailsDialog',
    'ProcessTokenStatus',
    'ReassignUserTaskButton',
    'ReassignUserTaskDialog',
    'ReportErrorServiceTaskButton',
    'ReportErrorServiceTaskDialog',
    'ReviewApplication',
    'ReviewScheduling',
    'ScheduleInterview',
    'ScheduleTrainingSession',
    'SelectCalledProcessField',
    'SelectRoleField',
    'SelectRoleNameField',
    'SelectServiceQueueField',
    'SelectUserField',
    'SelectUserInterfaceComponent',
    'SendMessageButton',
    'SendMessageDialog',
    'SetUpInterviews',
    'StaffTypeDetailsDialog',
    'StartInstanceDialog',
    'TokenList',
    'UnclaimUserTaskButton',
    'UpdateProcessInstanceDataButton',
    'UpdateProcessInstanceDataDialog',
    'UpdateUserTaskDataButton',
    'UpdateUserTaskDataDialog',
    'VenueDetailsDialog',
    'WaitForInterview',
    'WaitForInvitation',
    'WaitForTraining',
  ] as const;

  readonly metadata: readonly ComponentMetadata[] = [
    {
      name: 'AddButton',
      selector: 'add-button',
      displayName: 'Add Button',
      description:
        "A button that opens a dialog for adding a new record to a persisted array. Renders a Material raised button with an add icon. When clicked, opens an AddDialog that dynamically loads a schema-driven form for the array's entity type.",
      inputs: [
        { name: 'array', type: 'PersistedArray<any> | PersistedFormArray' },
        { name: 'label', type: 'string' },
        { name: 'dialogWidth', type: 'string' },
      ],
      outputs: [{ name: 'added', type: 'EventEmitter<any>' }],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'AddDialog',
      selector: 'add-dialog',
      displayName: 'Add Dialog',
      description:
        "Dialog for adding a new record to a persisted array. Dynamically loads a schema-driven form using BusinessObjectService based on the array's entity name. Hidden fields are automatically disabled so they don't block form validity.",
      inputs: [
        { name: 'array', type: 'PersistedArray<any> | PersistedFormArray' },
        { name: 'label', type: 'string' },
      ],
      outputs: [{ name: 'added', type: 'EventEmitter<any>' }],
      isDialog: true,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'AssignFunctionalArea',
      selector: 'assign-functional-area',
      displayName: 'Assign Functional Area',
      description:
        'Component for assigning a candidate to a functional area after interview.',
      inputs: [{ name: 'userTaskFormGroup', type: 'UserTaskFormGroup' }],
      outputs: [
        { name: 'completed', type: 'EventEmitter<void>' },
        { name: 'saveForLater', type: 'EventEmitter<void>' },
      ],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
      metadata: { userTaskComponent: true },
    },
    {
      name: 'Avatar',
      selector: 'avatar',
      displayName: 'Avatar',
      description:
        'User avatar button with a menu showing the current user email and logout. Shows a "Switch User" option when impersonation is enabled.',
      inputs: [],
      outputs: [],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'Breadcrumb',
      selector: 'breadcrumb',
      displayName: 'Breadcrumb',
      description:
        'Navigation breadcrumb container that displays child breadcrumb levels separated by delimiters.',
      inputs: [],
      outputs: [],
      isDialog: false,
      isCustomElement: false,
      allowChildren: true,
    },
    {
      name: 'BreadcrumbLevel',
      selector: 'breadcrumb-level',
      displayName: 'Breadcrumb Level',
      description:
        'A single level inside a Breadcrumb. Renders as a link when path is set, plain text otherwise.',
      inputs: [
        { name: 'label', type: 'string' },
        { name: 'path', type: 'string' },
        { name: 'queryParams', type: 'any' },
      ],
      outputs: [],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'CandidateApplicationForm',
      selector: 'candidate-application-form',
      displayName: 'Candidate Application Form',
      description: 'Reusable form component for candidate application data.',
      inputs: [
        { name: 'candidate', type: 'CandidateFormGroup' },
        { name: 'readOnly', type: 'boolean' },
      ],
      outputs: [],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'ClaimServiceTaskButton',
      selector: 'claim-service-task-button',
      displayName: 'Claim Service Task Button',
      description: 'Button that opens the claim service task dialog.',
      inputs: [{ name: 'serviceTaskFormGroup', type: 'ServiceTaskFormGroup' }],
      outputs: [{ name: 'claimed', type: 'EventEmitter<void>' }],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'ClaimServiceTaskDialog',
      selector: 'claim-service-task-dialog',
      displayName: 'Claim Service Task Dialog',
      description: 'Dialog for claiming a service task with a worker ID.',
      inputs: [{ name: 'serviceTaskFormGroup', type: 'ServiceTaskFormGroup' }],
      outputs: [{ name: 'claimed', type: 'EventEmitter<void>' }],
      isDialog: true,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'ClaimUserTaskButton',
      selector: 'claim-user-task-button',
      displayName: 'Claim User Task Button',
      description: 'Button that opens the claim user task dialog.',
      inputs: [{ name: 'userTaskFormGroup', type: 'UserTaskFormGroup' }],
      outputs: [{ name: 'claimed', type: 'EventEmitter<void>' }],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'ClaimUserTaskDialog',
      selector: 'claim-user-task-dialog',
      displayName: 'Claim User Task Dialog',
      description:
        'Dialog for claiming a user task. Admins can select a user to claim on behalf of.',
      inputs: [{ name: 'userTaskFormGroup', type: 'UserTaskFormGroup' }],
      outputs: [{ name: 'claimed', type: 'EventEmitter<void>' }],
      isDialog: true,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'CompleteApplication',
      selector: 'complete-application',
      displayName: 'Complete Application',
      description: 'Component for completing a candidate application.',
      inputs: [{ name: 'userTaskFormGroup', type: 'UserTaskFormGroup' }],
      outputs: [
        { name: 'completed', type: 'EventEmitter<void>' },
        { name: 'saveForLater', type: 'EventEmitter<void>' },
      ],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
      metadata: { userTaskComponent: true },
    },
    {
      name: 'CompleteServiceTaskButton',
      selector: 'complete-service-task-button',
      displayName: 'Complete Service Task Button',
      description: 'Button that opens the complete service task dialog.',
      inputs: [{ name: 'serviceTaskFormGroup', type: 'ServiceTaskFormGroup' }],
      outputs: [{ name: 'completed', type: 'EventEmitter<void>' }],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'CompleteServiceTaskDialog',
      selector: 'complete-service-task-dialog',
      displayName: 'Complete Service Task Dialog',
      description:
        'Dialog for completing a service task with optional output data.',
      inputs: [{ name: 'serviceTaskFormGroup', type: 'ServiceTaskFormGroup' }],
      outputs: [{ name: 'completed', type: 'EventEmitter<void>' }],
      isDialog: true,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'CompleteUserTaskButton',
      selector: 'complete-user-task-button',
      displayName: 'Complete User Task Button',
      description: 'Button that opens the complete user task dialog.',
      inputs: [{ name: 'userTaskFormGroup', type: 'UserTaskFormGroup' }],
      outputs: [{ name: 'completed', type: 'EventEmitter<void>' }],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'CompleteUserTaskDialog',
      selector: 'complete-user-task-dialog',
      displayName: 'Complete User Task Dialog',
      description:
        'Dialog for completing a user task with optional data updates.',
      inputs: [{ name: 'userTaskFormGroup', type: 'UserTaskFormGroup' }],
      outputs: [{ name: 'completed', type: 'EventEmitter<void>' }],
      isDialog: true,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'EmailTester',
      selector: 'email-tester',
      displayName: 'Email Tester',
      description: 'Test component for sending correspondence items.',
      inputs: [
        { name: 'userTaskFormGroup', type: 'UserTaskFormGroup' },
        { name: 'candidateId', type: 'number' },
        { name: 'senderId', type: 'number' },
      ],
      outputs: [{ name: 'completed', type: 'EventEmitter<void>' }],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
      metadata: { userTaskComponent: true },
    },
    {
      name: 'EvaluateInterview',
      selector: 'evaluate-interview',
      displayName: 'Evaluate Interview',
      description: 'Component for evaluating a completed interview.',
      inputs: [{ name: 'userTaskFormGroup', type: 'UserTaskFormGroup' }],
      outputs: [
        { name: 'completed', type: 'EventEmitter<void>' },
        { name: 'saveForLater', type: 'EventEmitter<void>' },
      ],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
      metadata: { userTaskComponent: true },
    },
    {
      name: 'FireTimerButton',
      selector: 'fire-timer-button',
      displayName: 'Fire Timer Button',
      description:
        'Button that opens the fire timer dialog to manually fire a timer.',
      inputs: [{ name: 'timerFormGroup', type: 'TimerFormGroup' }],
      outputs: [{ name: 'fired', type: 'EventEmitter<void>' }],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'FireTimerDialog',
      selector: 'fire-timer-dialog',
      displayName: 'Fire Timer Dialog',
      description: 'Confirmation dialog for manually firing a timer.',
      inputs: [{ name: 'timerFormGroup', type: 'TimerFormGroup' }],
      outputs: [{ name: 'fired', type: 'EventEmitter<void>' }],
      isDialog: true,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'HistoryDetailsDialog',
      selector: 'history-details-dialog',
      displayName: 'History Details Dialog',
      description: 'Dialog that displays details for a single history entry.',
      inputs: [
        { name: 'eventType', type: 'string' },
        { name: 'timestamp', type: 'string' },
        { name: 'data', type: 'any' },
      ],
      outputs: [],
      isDialog: true,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'HistoryList',
      selector: 'history-list',
      displayName: 'History List',
      description:
        'Displays a list of history entries with relative timestamps.',
      inputs: [
        { name: 'history', type: 'any' },
        { name: 'title', type: 'string' },
      ],
      outputs: [],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'LoadFromFilesButton',
      selector: 'load-from-files-button',
      displayName: 'Load From Files Button',
      description:
        'Button that loads process designs from server files and shows results in a dialog.',
      inputs: [],
      outputs: [{ name: 'loaded', type: 'EventEmitter<void>' }],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'LoadFromFilesDialog',
      selector: 'load-from-files-dialog',
      displayName: 'Load From Files Dialog',
      description:
        'Dialog showing results of loading process designs from server files.',
      inputs: [{ name: 'results', type: 'any[]' }],
      outputs: [],
      isDialog: true,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'ManageRoleAssignments',
      selector: 'manage-role-assignments',
      displayName: 'Manage Role Assignments',
      description:
        'Displays a table of users and their role assignments. Rows are users (derived from role assignments), columns are roles. Checkbox icon buttons toggle role assignments.',
      inputs: [{ name: 'roleNames', type: 'string' }],
      outputs: [],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'OrganizationDetailsDialog',
      selector: 'organization-details-dialog',
      displayName: 'Organization Details Dialog',
      description: 'Dialog for editing organization details.',
      inputs: [{ name: 'organization', type: 'OrganizationFormGroup' }],
      outputs: [{ name: 'saved', type: 'EventEmitter<void>' }],
      isDialog: true,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'OrgStructureLevelDetailsDialog',
      selector: 'org-structure-level-details-dialog',
      displayName: 'Org Structure Level Details Dialog',
      description: 'Dialog for editing org structure level details.',
      inputs: [
        { name: 'orgStructureLevel', type: 'OrgStructureLevelFormGroup' },
      ],
      outputs: [{ name: 'saved', type: 'EventEmitter<void>' }],
      isDialog: true,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'PerformInterview',
      selector: 'perform-interview',
      displayName: 'Perform Interview',
      description: 'Component for conducting a candidate interview.',
      inputs: [{ name: 'userTaskFormGroup', type: 'UserTaskFormGroup' }],
      outputs: [
        { name: 'completed', type: 'EventEmitter<void>' },
        { name: 'isApproved', type: 'EventEmitter<boolean>' },
        { name: 'saveForLater', type: 'EventEmitter<void>' },
      ],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
      metadata: { userTaskComponent: true },
    },
    {
      name: 'PositionDetailsDialog',
      selector: 'position-details-dialog',
      displayName: 'Position Details Dialog',
      description: 'Dialog for editing position details.',
      inputs: [{ name: 'position', type: 'PositionFormGroup' }],
      outputs: [{ name: 'saved', type: 'EventEmitter<void>' }],
      isDialog: true,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'ProcessTokenStatus',
      selector: 'process-token-status',
      displayName: 'Process Token Status',
      description: 'Component displaying the status of process tokens.',
      inputs: [{ name: 'designUuid', type: 'string' }],
      outputs: [
        { name: 'calledProcessSelected', type: 'EventEmitter<string>' },
        {
          name: 'activitySelected',
          type: 'EventEmitter<{ activityId: string; designUuid: string; } | null>',
        },
      ],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'ReassignUserTaskButton',
      selector: 'reassign-user-task-button',
      displayName: 'Reassign User Task Button',
      description: 'Button that opens the reassign user task dialog.',
      inputs: [{ name: 'userTaskFormGroup', type: 'UserTaskFormGroup' }],
      outputs: [{ name: 'reassigned', type: 'EventEmitter<void>' }],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'ReassignUserTaskDialog',
      selector: 'reassign-user-task-dialog',
      displayName: 'Reassign User Task Dialog',
      description: 'Dialog for reassigning a user task to a different user.',
      inputs: [{ name: 'userTaskFormGroup', type: 'UserTaskFormGroup' }],
      outputs: [{ name: 'reassigned', type: 'EventEmitter<void>' }],
      isDialog: true,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'ReportErrorServiceTaskButton',
      selector: 'report-error-service-task-button',
      displayName: 'Report Error Service Task Button',
      description:
        'Button that opens the report error dialog for a service task.',
      inputs: [{ name: 'serviceTaskFormGroup', type: 'ServiceTaskFormGroup' }],
      outputs: [{ name: 'errorReported', type: 'EventEmitter<void>' }],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'ReportErrorServiceTaskDialog',
      selector: 'report-error-service-task-dialog',
      displayName: 'Report Error Service Task Dialog',
      description: 'Dialog for reporting an error on a service task.',
      inputs: [{ name: 'serviceTaskFormGroup', type: 'ServiceTaskFormGroup' }],
      outputs: [{ name: 'errorReported', type: 'EventEmitter<void>' }],
      isDialog: true,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'ReviewApplication',
      selector: 'review-application',
      displayName: 'Review Application',
      description: 'Component for reviewing a candidate application.',
      inputs: [{ name: 'userTaskFormGroup', type: 'UserTaskFormGroup' }],
      outputs: [
        { name: 'completed', type: 'EventEmitter<void>' },
        { name: 'isApproved', type: 'EventEmitter<boolean>' },
        { name: 'saveForLater', type: 'EventEmitter<void>' },
      ],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
      metadata: { userTaskComponent: true },
    },
    {
      name: 'ReviewScheduling',
      selector: 'review-scheduling',
      displayName: 'Review Scheduling',
      description:
        'Component for reviewing interview scheduling progress and deciding whether to invite more candidates.',
      inputs: [
        { name: 'userTaskFormGroup', type: 'UserTaskFormGroup' },
        { name: 'interviewEventId', type: 'number' },
      ],
      outputs: [
        { name: 'completed', type: 'EventEmitter<void>' },
        { name: 'saveForLater', type: 'EventEmitter<void>' },
      ],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
      metadata: { userTaskComponent: true },
    },
    {
      name: 'ScheduleInterview',
      selector: 'schedule-interview',
      displayName: 'Schedule Interview',
      description: 'Component for scheduling a candidate interview.',
      inputs: [
        { name: 'userTaskFormGroup', type: 'UserTaskFormGroup' },
        { name: 'interviewEventDate', type: 'string' },
      ],
      outputs: [
        { name: 'completed', type: 'EventEmitter<void>' },
        { name: 'saveForLater', type: 'EventEmitter<void>' },
      ],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
      metadata: { userTaskComponent: true },
    },
    {
      name: 'ScheduleTrainingSession',
      selector: 'schedule-training-session',
      displayName: 'Schedule Training Session',
      description: 'Component for scheduling a candidate training session.',
      inputs: [{ name: 'userTaskFormGroup', type: 'UserTaskFormGroup' }],
      outputs: [
        { name: 'completed', type: 'EventEmitter<void>' },
        { name: 'saveForLater', type: 'EventEmitter<void>' },
      ],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
      metadata: { userTaskComponent: true },
    },
    {
      name: 'SelectCalledProcessField',
      selector: 'select-called-process-field',
      displayName: 'Select Called Process Field',
      description:
        'Schema form field that displays a dropdown of active process designs. The control value is the process design UUID.',
      inputs: [
        { name: 'control', type: 'SchemaFormControl' },
        { name: 'label', type: 'string' },
        { name: 'placeholder', type: 'string' },
        { name: 'hideHelpText', type: 'boolean' },
      ],
      outputs: [],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
      metadata: { schemaFormsField: true },
    },
    {
      name: 'SelectRoleField',
      selector: 'select-role-field',
      displayName: 'Select Role Field',
      description:
        'Schema form field that displays a dropdown of roles. The control value is the role id.',
      inputs: [
        { name: 'control', type: 'SchemaFormControl' },
        { name: 'label', type: 'string' },
        { name: 'placeholder', type: 'string' },
        { name: 'hideHelpText', type: 'boolean' },
      ],
      outputs: [],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
      metadata: { schemaFormsField: true },
    },
    {
      name: 'SelectRoleNameField',
      selector: 'select-role-name-field',
      displayName: 'Select Role Name Field',
      description:
        'Schema form field that displays a dropdown of roles. The control value is the role name.',
      inputs: [
        { name: 'control', type: 'SchemaFormControl' },
        { name: 'label', type: 'string' },
        { name: 'placeholder', type: 'string' },
        { name: 'hideHelpText', type: 'boolean' },
      ],
      outputs: [],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
      metadata: { schemaFormsField: true },
    },
    {
      name: 'SelectServiceQueueField',
      selector: 'select-service-queue-field',
      displayName: 'Select Service Queue Field',
      description:
        'Schema form field that displays a dropdown of service task queue names. Queue names are derived from behavior metadata with serviceTaskQueue. The control value is the queue name.',
      inputs: [
        { name: 'control', type: 'SchemaFormControl' },
        { name: 'label', type: 'string' },
        { name: 'placeholder', type: 'string' },
        { name: 'hideHelpText', type: 'boolean' },
      ],
      outputs: [],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
      metadata: { schemaFormsField: true },
    },
    {
      name: 'SelectUserField',
      selector: 'select-user-field',
      displayName: 'Select User Field',
      description:
        'Schema form field that displays a dropdown of users. The control value is the user id.',
      inputs: [
        { name: 'control', type: 'SchemaFormControl' },
        { name: 'label', type: 'string' },
        { name: 'placeholder', type: 'string' },
        { name: 'hideHelpText', type: 'boolean' },
      ],
      outputs: [],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
      metadata: { schemaFormsField: true },
    },
    {
      name: 'SelectUserInterfaceComponent',
      selector: 'select-user-interface-component',
      displayName: 'Select User Interface Component',
      description:
        'Schema form field that displays a dropdown of components which accept a userTaskFormGroup input. The control value is the component name.',
      inputs: [
        { name: 'control', type: 'SchemaFormControl' },
        { name: 'label', type: 'string' },
        { name: 'placeholder', type: 'string' },
        { name: 'hideHelpText', type: 'boolean' },
      ],
      outputs: [],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
      metadata: { schemaFormsField: true },
    },
    {
      name: 'SendMessageButton',
      selector: 'send-message-button',
      displayName: 'Send Message Button',
      description:
        'Button that opens the send message dialog to trigger a message listener.',
      inputs: [
        { name: 'messageListenerFormGroup', type: 'MessageListenerFormGroup' },
      ],
      outputs: [{ name: 'sent', type: 'EventEmitter<void>' }],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'SendMessageDialog',
      selector: 'send-message-dialog',
      displayName: 'Send Message Dialog',
      description:
        'Dialog for triggering a message listener with optional data.',
      inputs: [
        { name: 'messageListenerFormGroup', type: 'MessageListenerFormGroup' },
      ],
      outputs: [{ name: 'sent', type: 'EventEmitter<void>' }],
      isDialog: true,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'SetUpInterviews',
      selector: 'set-up-interviews',
      displayName: 'Set Up Interviews',
      description:
        'Component for configuring the number of interview slots and date.',
      inputs: [{ name: 'userTaskFormGroup', type: 'UserTaskFormGroup' }],
      outputs: [
        { name: 'completed', type: 'EventEmitter<void>' },
        { name: 'saveForLater', type: 'EventEmitter<void>' },
      ],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
      metadata: { userTaskComponent: true },
    },
    {
      name: 'StaffTypeDetailsDialog',
      selector: 'staff-type-details-dialog',
      displayName: 'Staff Type Details Dialog',
      description: 'Dialog for editing staff type details.',
      inputs: [{ name: 'staffType', type: 'StaffTypeFormGroup' }],
      outputs: [{ name: 'saved', type: 'EventEmitter<void>' }],
      isDialog: true,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'StartInstanceDialog',
      selector: 'start-instance-dialog',
      displayName: 'Start Instance Dialog',
      description:
        'Dialog for starting a new process instance with optional initial data.',
      inputs: [{ name: 'processDesign', type: 'ProcessDesign' }],
      outputs: [
        { name: 'started', type: 'EventEmitter<any>' },
        { name: 'startedAndRedirect', type: 'EventEmitter<any>' },
      ],
      isDialog: true,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'TokenList',
      selector: 'token-list',
      displayName: 'Token List',
      description: 'Displays a list of tokens with activity names and status.',
      inputs: [
        { name: 'tokens', type: 'any[]' },
        { name: 'title', type: 'string' },
        { name: 'queryParams', type: 'any' },
        { name: 'activities', type: 'any[]' },
      ],
      outputs: [],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'UnclaimUserTaskButton',
      selector: 'unclaim-user-task-button',
      displayName: 'Unclaim User Task Button',
      description:
        'Button that unclaims a user task directly without a dialog.',
      inputs: [{ name: 'userTaskFormGroup', type: 'UserTaskFormGroup' }],
      outputs: [{ name: 'unclaimed', type: 'EventEmitter<void>' }],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'UpdateProcessInstanceDataButton',
      selector: 'update-process-instance-data-button',
      displayName: 'Update Process Instance Data Button',
      description:
        'Button that opens the update data dialog for a process instance.',
      inputs: [{ name: 'processInstance', type: 'ProcessInstanceFormGroup' }],
      outputs: [],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'UpdateProcessInstanceDataDialog',
      selector: 'update-process-instance-data-dialog',
      displayName: 'Update Process Instance Data Dialog',
      description: 'Dialog for updating data on a process instance.',
      inputs: [{ name: 'processInstance', type: 'ProcessInstanceFormGroup' }],
      outputs: [],
      isDialog: true,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'UpdateUserTaskDataButton',
      selector: 'update-user-task-data-button',
      displayName: 'Update User Task Data Button',
      description: 'Button that opens the update data dialog for a user task.',
      inputs: [{ name: 'userTaskFormGroup', type: 'UserTaskFormGroup' }],
      outputs: [{ name: 'dataUpdated', type: 'EventEmitter<void>' }],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'UpdateUserTaskDataDialog',
      selector: 'update-user-task-data-dialog',
      displayName: 'Update User Task Data Dialog',
      description: 'Dialog for updating data on a user task.',
      inputs: [{ name: 'userTaskFormGroup', type: 'UserTaskFormGroup' }],
      outputs: [{ name: 'dataUpdated', type: 'EventEmitter<void>' }],
      isDialog: true,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'VenueDetailsDialog',
      selector: 'venue-details-dialog',
      displayName: 'Venue Details Dialog',
      description: 'Dialog for editing venue details.',
      inputs: [{ name: 'venue', type: 'VenueFormGroup' }],
      outputs: [{ name: 'saved', type: 'EventEmitter<void>' }],
      isDialog: true,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'WaitForInterview',
      selector: 'wait-for-interview',
      displayName: 'Wait for Interview',
      description:
        'Displayed while the candidate waits for their scheduled interview.',
      inputs: [
        { name: 'userTaskFormGroup', type: 'UserTaskFormGroup' },
        { name: 'interviewDate', type: 'string' },
      ],
      outputs: [
        { name: 'saveForLater', type: 'EventEmitter<void>' },
        { name: 'completed', type: 'EventEmitter<void>' },
      ],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
      metadata: { userTaskComponent: true },
    },
    {
      name: 'WaitForInvitation',
      selector: 'wait-for-invitation',
      displayName: 'Wait for Invitation',
      description:
        'Displayed while the candidate waits for interview slots to become available.',
      inputs: [{ name: 'userTaskFormGroup', type: 'UserTaskFormGroup' }],
      outputs: [
        { name: 'saveForLater', type: 'EventEmitter<void>' },
        { name: 'completed', type: 'EventEmitter<void>' },
      ],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
      metadata: { userTaskComponent: true },
    },
    {
      name: 'WaitForTraining',
      selector: 'wait-for-training',
      displayName: 'Wait for Training',
      description:
        'Displayed while the candidate waits for their scheduled training session.',
      inputs: [
        { name: 'userTaskFormGroup', type: 'UserTaskFormGroup' },
        { name: 'trainingDate', type: 'string' },
      ],
      outputs: [
        { name: 'saveForLater', type: 'EventEmitter<void>' },
        { name: 'completed', type: 'EventEmitter<void>' },
      ],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
      metadata: { userTaskComponent: true },
    },
  ];

  getMetadata(name: string): ComponentMetadata | undefined {
    return this.metadata.find((m) => m.name === name);
  }

  async loadComponent(name: string): Promise<Type<any>> {
    debug('loadComponent %s', name);
    switch (name) {
      case 'AddButton':
        return import('../../components/add-button/add-button.component').then(
          (m) => m.AddButtonComponent,
        );
      case 'AddDialog':
        return import('../../components/add-dialog/add-dialog.component').then(
          (m) => m.AddDialogComponent,
        );
      case 'AssignFunctionalArea':
        return import('../../components/assign-functional-area/assign-functional-area.component').then(
          (m) => m.AssignFunctionalAreaComponent,
        );
      case 'Avatar':
        return import('../../components/avatar/avatar.component').then(
          (m) => m.AvatarComponent,
        );
      case 'Breadcrumb':
        return import('../../components/breadcrumb/breadcrumb.component').then(
          (m) => m.BreadcrumbComponent,
        );
      case 'BreadcrumbLevel':
        return import('../../components/breadcrumb-level/breadcrumb-level.component').then(
          (m) => m.BreadcrumbLevelComponent,
        );
      case 'CandidateApplicationForm':
        return import('../../components/candidate-application-form/candidate-application-form.component').then(
          (m) => m.CandidateApplicationFormComponent,
        );
      case 'ClaimServiceTaskButton':
        return import('../../components/claim-service-task-button/claim-service-task-button.component').then(
          (m) => m.ClaimServiceTaskButtonComponent,
        );
      case 'ClaimServiceTaskDialog':
        return import('../../components/claim-service-task-dialog/claim-service-task-dialog.component').then(
          (m) => m.ClaimServiceTaskDialogComponent,
        );
      case 'ClaimUserTaskButton':
        return import('../../components/claim-user-task-button/claim-user-task-button.component').then(
          (m) => m.ClaimUserTaskButtonComponent,
        );
      case 'ClaimUserTaskDialog':
        return import('../../components/claim-user-task-dialog/claim-user-task-dialog.component').then(
          (m) => m.ClaimUserTaskDialogComponent,
        );
      case 'CompleteApplication':
        return import('../../components/complete-application/complete-application.component').then(
          (m) => m.CompleteApplicationComponent,
        );
      case 'CompleteServiceTaskButton':
        return import('../../components/complete-service-task-button/complete-service-task-button.component').then(
          (m) => m.CompleteServiceTaskButtonComponent,
        );
      case 'CompleteServiceTaskDialog':
        return import('../../components/complete-service-task-dialog/complete-service-task-dialog.component').then(
          (m) => m.CompleteServiceTaskDialogComponent,
        );
      case 'CompleteUserTaskButton':
        return import('../../components/complete-user-task-button/complete-user-task-button.component').then(
          (m) => m.CompleteUserTaskButtonComponent,
        );
      case 'CompleteUserTaskDialog':
        return import('../../components/complete-user-task-dialog/complete-user-task-dialog.component').then(
          (m) => m.CompleteUserTaskDialogComponent,
        );
      case 'EmailTester':
        return import('../../components/email-tester/email-tester.component').then(
          (m) => m.EmailTesterComponent,
        );
      case 'EvaluateInterview':
        return import('../../components/evaluate-interview/evaluate-interview.component').then(
          (m) => m.EvaluateInterviewComponent,
        );
      case 'FireTimerButton':
        return import('../../components/fire-timer-button/fire-timer-button.component').then(
          (m) => m.FireTimerButtonComponent,
        );
      case 'FireTimerDialog':
        return import('../../components/fire-timer-dialog/fire-timer-dialog.component').then(
          (m) => m.FireTimerDialogComponent,
        );
      case 'HistoryDetailsDialog':
        return import('../../components/history-details-dialog/history-details-dialog.component').then(
          (m) => m.HistoryDetailsDialogComponent,
        );
      case 'HistoryList':
        return import('../../components/history-list/history-list.component').then(
          (m) => m.HistoryListComponent,
        );
      case 'LoadFromFilesButton':
        return import('../../components/load-from-files-button/load-from-files-button.component').then(
          (m) => m.LoadFromFilesButtonComponent,
        );
      case 'LoadFromFilesDialog':
        return import('../../components/load-from-files-dialog/load-from-files-dialog.component').then(
          (m) => m.LoadFromFilesDialogComponent,
        );
      case 'ManageRoleAssignments':
        return import('../../components/manage-role-assignments/manage-role-assignments.component').then(
          (m) => m.ManageRoleAssignmentsComponent,
        );
      case 'OrganizationDetailsDialog':
        return import('../../components/organization-details-dialog/organization-details-dialog.component').then(
          (m) => m.OrganizationDetailsDialogComponent,
        );
      case 'OrgStructureLevelDetailsDialog':
        return import('../../components/org-structure-level-details-dialog/org-structure-level-details-dialog.component').then(
          (m) => m.OrgStructureLevelDetailsDialogComponent,
        );
      case 'PerformInterview':
        return import('../../components/perform-interview/perform-interview.component').then(
          (m) => m.PerformInterviewComponent,
        );
      case 'PositionDetailsDialog':
        return import('../../components/position-details-dialog/position-details-dialog.component').then(
          (m) => m.PositionDetailsDialogComponent,
        );
      case 'ProcessTokenStatus':
        return import('../../components/process-token-status/process-token-status.component').then(
          (m) => m.ProcessTokenStatusComponent,
        );
      case 'ReassignUserTaskButton':
        return import('../../components/reassign-user-task-button/reassign-user-task-button.component').then(
          (m) => m.ReassignUserTaskButtonComponent,
        );
      case 'ReassignUserTaskDialog':
        return import('../../components/reassign-user-task-dialog/reassign-user-task-dialog.component').then(
          (m) => m.ReassignUserTaskDialogComponent,
        );
      case 'ReportErrorServiceTaskButton':
        return import('../../components/report-error-service-task-button/report-error-service-task-button.component').then(
          (m) => m.ReportErrorServiceTaskButtonComponent,
        );
      case 'ReportErrorServiceTaskDialog':
        return import('../../components/report-error-service-task-dialog/report-error-service-task-dialog.component').then(
          (m) => m.ReportErrorServiceTaskDialogComponent,
        );
      case 'ReviewApplication':
        return import('../../components/review-application/review-application.component').then(
          (m) => m.ReviewApplicationComponent,
        );
      case 'ReviewScheduling':
        return import('../../components/review-scheduling/review-scheduling.component').then(
          (m) => m.ReviewSchedulingComponent,
        );
      case 'ScheduleInterview':
        return import('../../components/schedule-interview/schedule-interview.component').then(
          (m) => m.ScheduleInterviewComponent,
        );
      case 'ScheduleTrainingSession':
        return import('../../components/schedule-training-session/schedule-training-session.component').then(
          (m) => m.ScheduleTrainingSessionComponent,
        );
      case 'SelectCalledProcessField':
        return import('../../components/select-called-process-field/select-called-process-field.component').then(
          (m) => m.SelectCalledProcessFieldComponent,
        );
      case 'SelectRoleField':
        return import('../../components/select-role-field/select-role-field.component').then(
          (m) => m.SelectRoleFieldComponent,
        );
      case 'SelectRoleNameField':
        return import('../../components/select-role-name-field/select-role-name-field.component').then(
          (m) => m.SelectRoleNameFieldComponent,
        );
      case 'SelectServiceQueueField':
        return import('../../components/select-service-queue-field/select-service-queue-field.component').then(
          (m) => m.SelectServiceQueueFieldComponent,
        );
      case 'SelectUserField':
        return import('../../components/select-user-field/select-user-field.component').then(
          (m) => m.SelectUserFieldComponent,
        );
      case 'SelectUserInterfaceComponent':
        return import('../../components/select-user-interface-component/select-user-interface-component.component').then(
          (m) => m.SelectUserInterfaceComponentComponent,
        );
      case 'SendMessageButton':
        return import('../../components/send-message-button/send-message-button.component').then(
          (m) => m.SendMessageButtonComponent,
        );
      case 'SendMessageDialog':
        return import('../../components/send-message-dialog/send-message-dialog.component').then(
          (m) => m.SendMessageDialogComponent,
        );
      case 'SetUpInterviews':
        return import('../../components/set-up-interviews/set-up-interviews.component').then(
          (m) => m.SetUpInterviewsComponent,
        );
      case 'StaffTypeDetailsDialog':
        return import('../../components/staff-type-details-dialog/staff-type-details-dialog.component').then(
          (m) => m.StaffTypeDetailsDialogComponent,
        );
      case 'StartInstanceDialog':
        return import('../../components/start-instance-dialog/start-instance-dialog.component').then(
          (m) => m.StartInstanceDialogComponent,
        );
      case 'TokenList':
        return import('../../components/token-list/token-list.component').then(
          (m) => m.TokenListComponent,
        );
      case 'UnclaimUserTaskButton':
        return import('../../components/unclaim-user-task-button/unclaim-user-task-button.component').then(
          (m) => m.UnclaimUserTaskButtonComponent,
        );
      case 'UpdateProcessInstanceDataButton':
        return import('../../components/update-process-instance-data-button/update-process-instance-data-button.component').then(
          (m) => m.UpdateProcessInstanceDataButtonComponent,
        );
      case 'UpdateProcessInstanceDataDialog':
        return import('../../components/update-process-instance-data-dialog/update-process-instance-data-dialog.component').then(
          (m) => m.UpdateProcessInstanceDataDialogComponent,
        );
      case 'UpdateUserTaskDataButton':
        return import('../../components/update-user-task-data-button/update-user-task-data-button.component').then(
          (m) => m.UpdateUserTaskDataButtonComponent,
        );
      case 'UpdateUserTaskDataDialog':
        return import('../../components/update-user-task-data-dialog/update-user-task-data-dialog.component').then(
          (m) => m.UpdateUserTaskDataDialogComponent,
        );
      case 'VenueDetailsDialog':
        return import('../../components/venue-details-dialog/venue-details-dialog.component').then(
          (m) => m.VenueDetailsDialogComponent,
        );
      case 'WaitForInterview':
        return import('../../components/wait-for-interview/wait-for-interview.component').then(
          (m) => m.WaitForInterviewComponent,
        );
      case 'WaitForInvitation':
        return import('../../components/wait-for-invitation/wait-for-invitation.component').then(
          (m) => m.WaitForInvitationComponent,
        );
      case 'WaitForTraining':
        return import('../../components/wait-for-training/wait-for-training.component').then(
          (m) => m.WaitForTrainingComponent,
        );
      default:
        throw new Error(`Unknown component: ${name}`);
    }
  }
}
